import type { CSSProperties } from 'react';
import { categoriaDeLlamada, ETIQUETAS_CATEGORIA_LLAMADA, modulesOf, type CategoriaLlamada, type InsightsLlamadas, type Snapshot } from '../lib/snapshot-types.js';
import { eurosCompacto, porcentaje, unDecimal } from '../lib/etiquetas-metricas.js';
import { hashDeRuta, navigate } from '../lib/hash-route.js';

/**
 * EL EMBUDO DE VENTAS (revamp del 6-oct): se queda lo que gustaba (las mismas
 * etapas, recuentos e importes, en el orden del pipeline) con mas vida:
 *
 *   - Es lo abierto AHORA (la unica cifra del panel que no sigue al periodo, y lo
 *     dice: "· ahora"). Sin Ganada ni Perdida, que no son etapas de lo abierto.
 *   - Arriba, la tasa de lead a demo DEL PERIODO como "x de y (z %)", con un guion
 *     si no hubo leads.
 *   - Cada etapa es una barra en forma de flecha, con un degradado que avanza por
 *     el orden de las etapas, su recuento y su parte de lo abierto. Las barras
 *     crecen al montar (nada con `prefers-reduced-motion`) y se mueven al cambiar.
 *   - Al pasar por una etapa (o con el foco) se lee su importe; al pulsarla se abre
 *     la lista de sus oportunidades.
 *   - Debajo, el embudo de llamadas (llamadas, conversaciones, interes o demo y demo)
 *     y una linea con cada resultado y su lista. Una llamada pertenece a una categoria.
 */

interface Etapa { nombre: string; n: number; importe: number | null }

/** Las etapas abiertas del embudo, sin Ganada ni Perdida (siempre salian a 0). */
export function etapasAbiertas(s: Snapshot): Etapa[] {
  const cerradas = new Set(s.pipeline_card.stages.filter((st) => st.is_won || st.is_lost).map((st) => st.stage_name));
  return s.opportunities.funnel
    .filter((f) => !cerradas.has(f.stage_name))
    .map((f) => ({ nombre: f.stage_name, n: f.count, importe: typeof f.total_value === 'number' ? f.total_value : null }));
}

/** "4 de 33 (12 %)", o null sin leads (se pinta un guion). */
export function textoLeadDemo(ld: NonNullable<Snapshot['lead_demo']>): string | null {
  if (ld.status !== 'ok' || ld.leads === 0 || ld.tasa === null) return null;
  return `${ld.con_demo} de ${ld.leads} (${porcentaje(ld.tasa * 100, 0)})`;
}

function LeadDemo({ ld }: { ld: NonNullable<Snapshot['lead_demo']> }) {
  const texto = textoLeadDemo(ld);
  const ayuda = ld.status !== 'ok'
    ? 'No se han podido leer las citas'
    : ld.leads === 0
      ? 'Sin leads en el periodo'
      : `en el periodo · de ellas, hechas: ${ld.hechas}`;
  return (
    <div className="emb-ld" title="Oportunidades creadas en el periodo cuyo contacto tiene, desde ese día, una cita de GHL, una reunión grabada con Fluxo para Meet o su oportunidad ya en Meeting, propuesta o ganada">
      <span className="emb-ld-rotulo">Lead a demo</span>
      <strong className="emb-ld-valor">{texto ?? '-'}</strong>
      {texto && ld.tasa !== null && <span className="emb-ld-barra" aria-hidden="true"><i style={{ width: `${Math.min(100, ld.tasa * 100)}%` }} /></span>}
      <span className="emb-ld-ayuda">
        {ayuda}
        {ld.status === 'ok' && ld.sin_fecha > 0 && <> · {ld.sin_fecha} sin fecha de alta</>}
      </span>
    </div>
  );
}

export function EmbudoVentas({ snapshot, scope = 'team' }: { snapshot: Snapshot; scope?: 'me' | 'team' }) {
  const o = snapshot.opportunities;
  const pipeline = snapshot.pipeline_card;
  const etapas = etapasAbiertas(snapshot);
  const abierto = etapas.reduce((a, e) => a + e.n, 0);
  const max = Math.max(1, ...etapas.map((e) => e.n));
  const ld = snapshot.lead_demo;
  const importeTotal = o.summary.total_open_value;
  return (
    <section className="section emb" aria-label="Embudo de ventas">
      <div className="seccion-cabeza">
        <h2 className="section-title">Embudo de ventas{snapshot.filtro ? <span className="seccion-periodo"> · ahora</span> : null}</h2>
      </div>
      {ld && <LeadDemo ld={ld} />}
      {pipeline.status === 'unavailable' ? (
        <p className="section-empty">El embudo de ventas aún no se ha sincronizado con GHL.</p>
      ) : etapas.length === 0 ? (
        <p className="section-empty">Sin etapas definidas en el pipeline.</p>
      ) : (
        <>
          <ol className="emb-etapas">
            {etapas.map((e, i) => {
              const parte = abierto > 0 ? (100 * e.n) / abierto : 0;
              const estilo = {
                '--emb-pos': etapas.length > 1 ? i / (etapas.length - 1) : 0,
                '--emb-ancho': `${Math.max(e.n > 0 ? 7 : 0, (100 * e.n) / max)}%`,
                '--emb-retraso': `${i * 50}ms`,
              } as CSSProperties;
              const ayuda = `${e.nombre}: ${e.n} oportunidad${e.n === 1 ? '' : 'es'}${e.importe ? ` · ${eurosCompacto(e.importe)}` : ''} · ${porcentaje(parte)} de lo abierto`;
              return (
                <li key={e.nombre} className="emb-etapa">
                  <button
                    type="button"
                    className="emb-fila"
                    style={estilo}
                    disabled={e.n === 0}
                    aria-label={ayuda}
                    onClick={() => navigate({ kind: 'opps-list', stage: e.nombre, scope })}
                  >
                    <span className="emb-nombre">{e.nombre}</span>
                    <span className="emb-pista"><span className="emb-barra" /></span>
                    <span className="emb-n">{e.n}</span>
                    <span className="emb-parte">{porcentaje(parte)}</span>
                    <span className="emb-importe">{e.importe ? eurosCompacto(e.importe) : '-'}</span>
                    <span className="emb-ayuda" role="tooltip">{ayuda}</span>
                  </button>
                </li>
              );
            })}
          </ol>
          <div className="emb-pie">
            <span><strong>{abierto}</strong> abiertas · <strong>{eurosCompacto(importeTotal)}</strong> en el embudo</span>
            <span>Mediana hasta ganar: <strong>{o.velocity.median_days_to_won == null ? '-' : `${unDecimal(o.velocity.median_days_to_won)} días`}</strong></span>
            <span>Mediana en la etapa actual: <strong>{o.velocity.median_days_in_current_stage == null ? '-' : `${unDecimal(o.velocity.median_days_in_current_stage)} días`}</strong></span>
          </div>
        </>
      )}
      {modulesOf(snapshot).calls && <ResultadosLlamadas calls={snapshot.calls} />}
    </section>
  );
}

const cuenta = (n: unknown): n is number => Number.isSafeInteger(n) && (n as number) >= 0;

/** Solo se dibuja una partición completa de la población, nunca una muestra. */
function resultadosConfirmados(calls: Snapshot['calls']): InsightsLlamadas | null {
  const r = calls.insights;
  if (!calls.available || !r || r.status !== 'ok' || !cuenta(r.total) || r.total !== calls.total_calls || !Array.isArray(r.distribution)) return null;
  if (![r.with_result, r.with_transcript, r.with_objection, r.unknown].every((n) => cuenta(n) && n <= r.total)) return null;
  if (r.with_result + r.unknown !== r.total) return null;
  const claves = new Set<string>(); let total = 0; let desconocidas = 0;
  for (const d of r.distribution) {
    if (!d || !categoriaDeLlamada(d.key) || claves.has(d.key) || d.label !== ETIQUETAS_CATEGORIA_LLAMADA[d.key] || !cuenta(d.count) || d.count > r.total) return null;
    const esperado = r.total > 0 ? Math.round(1000 * d.count / r.total) / 10 : null;
    if (d.pct !== esperado) return null;
    claves.add(d.key); total += d.count;
    if (d.key === 'sin_informacion') desconocidas = d.count;
  }
  return total === r.total && desconocidas === r.unknown ? r : null;
}

/** Las categorias en las que se hablo con la persona: la base del embudo de llamadas. */
const CONVERSACION: readonly CategoriaLlamada[] = ['negativa', 'otro_momento', 'interes', 'demo_acordada'];
const INTERES_O_DEMO: readonly CategoriaLlamada[] = ['interes', 'demo_acordada'];

export interface PasoLlamadas {
  nombre: string;
  n: number;
  /** Parte sobre la base del paso (null sin base: se pinta un guion). */
  parte: number | null;
  base: 'llamadas' | 'conversaciones';
  /**
   * La lista que abre la fila, solo si cuenta exactamente lo mismo que la fila: todas las llamadas
   * o una categoria. «Conversaciones» e «Interes o demo» juntan varias y la lista solo filtra por
   * una, asi que no enlazan (cada categoria sigue a un clic en la linea de debajo).
   */
  lista?: { categoria?: CategoriaLlamada };
}

/**
 * EL EMBUDO DE LLAMADAS (15-oct, sustituye a los tres anillos): de las llamadas a la demo.
 * Los anillos pintaban las tres categorias mas grandes y en llamadas en frio las dos primeras
 * siempre eran «sin conversacion» y «sin informacion» (con los datos de Fluxo, 58,7 % y 17,9 %):
 * median el ruido. El embudo cuenta lo que se puede mover: cuantas llamadas acaban en
 * conversacion y cuantas conversaciones en interes o demo.
 */
export function pasosDeLlamadas(r: InsightsLlamadas): PasoLlamadas[] {
  const suma = (claves: readonly CategoriaLlamada[]) => r.distribution.filter((d) => claves.includes(d.key)).reduce((a, d) => a + d.count, 0);
  const conversaciones = suma(CONVERSACION);
  const sobre = (n: number, base: number) => (base > 0 ? (100 * n) / base : null);
  return [
    { nombre: 'Llamadas', n: r.total, parte: r.total > 0 ? 100 : null, base: 'llamadas', lista: {} },
    { nombre: 'Conversaciones', n: conversaciones, parte: sobre(conversaciones, r.total), base: 'llamadas' },
    { nombre: 'Interés o demo', n: suma(INTERES_O_DEMO), parte: sobre(suma(INTERES_O_DEMO), conversaciones), base: 'conversaciones' },
    { nombre: 'Demo acordada', n: suma(['demo_acordada']), parte: sobre(suma(['demo_acordada']), conversaciones), base: 'conversaciones', lista: { categoria: 'demo_acordada' } },
  ];
}

function ResultadosLlamadas({ calls }: { calls: Snapshot['calls'] }) {
  const r = resultadosConfirmados(calls);
  const vacio = !r ? 'Datos de llamadas no confirmados.' : r.total === 0 ? 'Sin llamadas en este periodo. No hay base para calcular porcentajes.' : null;
  const pasos = r && !vacio ? pasosDeLlamadas(r) : [];
  const desglose = r ? r.distribution.filter((d) => d.count > 0).sort((a, b) => b.count - a.count || a.key.localeCompare(b.key)) : [];
  return (
    <div className="emb-objeciones emb-llamadas">
      <h3 className="reu-sub">Embudo de llamadas</h3>
      {vacio ? <p className="section-empty">{vacio}</p> : r && (
        <>
          <ol className="emb-etapas">
            {pasos.map((p, i) => {
              const estilo = {
                '--emb-pos': i / (pasos.length - 1),
                '--emb-ancho': `${Math.max(p.n > 0 ? 2 : 0, (100 * p.n) / r.total)}%`,
                '--emb-retraso': `${i * 50}ms`,
              } as CSSProperties;
              const parte = p.parte === null ? '-' : porcentaje(p.parte);
              const ayuda = i === 0 ? `${p.n} llamadas del periodo` : `${p.nombre}: ${p.n} · ${parte} de las ${p.base}`;
              const contenido = (
                <>
                  <span className="emb-nombre">{p.nombre}</span>
                  <span className="emb-pista"><span className="emb-barra" /></span>
                  <span className="emb-n">{p.n}</span>
                  <span className="emb-parte">{i === 0 ? '' : p.parte === null ? '-' : `${parte}${p.base === 'conversaciones' ? ' conv.' : ''}`}</span>
                </>
              );
              return (
                <li key={p.nombre} className="emb-etapa">
                  {p.lista
                    ? <a className="emb-fila emb-fila--llamadas" style={estilo} href={hashDeRuta({ kind: 'calls-list', disposition: 'todas', ...p.lista })} aria-label={ayuda} title={ayuda}>{contenido}</a>
                    : <div className="emb-fila emb-fila--llamadas emb-fila--sin-enlace" style={estilo} title={ayuda}>{contenido}</div>}
                </li>
              );
            })}
          </ol>
          <nav className="emb-desglose" aria-label="Llamadas por resultado">
            {desglose.map((d) => (
              <a key={d.key} href={hashDeRuta({ kind: 'calls-list', disposition: 'todas', categoria: d.key })} title={`${d.label}: ${d.count} de ${r.total} llamadas`}>
                {d.label} <strong>{d.count}</strong>
              </a>
            ))}
          </nav>
        </>
      )}
    </div>
  );
}
